/**
 * AC-ADM-3: Persona Management (REAL)
 *
 * GraphQL operations:
 *   Query:    AcListPersonas → listPersonas[] { code displayName modules }
 *   Mutations: AcAssignPersonas, AcUpsertPersonaModule, AcDeactivatePersonaModule
 *
 * Schema-miss behavior: if listPersonas is not yet on the schema, the page
 * displays an amber note and "No personas found" (no currentUser fallback).
 */
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation } from "@apollo/client";
import { ChevronDown, ChevronRight, Pencil, ToggleLeft, ToggleRight } from "lucide-react";

import LoadingSpinner from "../../components/LoadingSpinner";
import { AdminWall, isAuthWallError } from "../components/AdminWall";
import SearchableSelect, { type SelectOption } from "../components/SearchableSelect";
import { catalogActions, FEATURE_CATALOG, type ModuleId } from "../../config/featureCatalog";
import {
  LIST_PERSONAS_QUERY,
  PERSONA_MODULE_OPTIONS_QUERY,
  UPSERT_PERSONA_MODULE_MUTATION,
  DEACTIVATE_PERSONA_MODULE_MUTATION,
  type ListPersonasResult,
  type PersonaModuleOptionsResult,
  type AcAdminPersonaModule,
  type AcAdminPersona,
  type UpsertPersonaModuleVars,
  type DeactivatePersonaModuleVars,
  type DeactivatePersonaModuleResult,
} from "../graphql/adminOps";

// ---- Upsert Module Modal ----
// Deterministic inputs: moduleId is a searchable single-select, actions is a searchable
// multiselect (module-aware — merges the FE feature catalog with values in use), data
// scope is structured (requireIpaMatch toggle + line-of-business multiselect). Sort order
// was removed from the UI — the backend keeps the existing value on edit and new modules
// append after the current max (computed by the caller).
function UpsertModuleModal({
  personaCode,
  existingModule,
  nextSortOrder,
  moduleIdOptions,
  actionCatalog,
  lobOptions,
  onClose,
  onSaved,
}: {
  personaCode: string;
  existingModule?: AcAdminPersonaModule;
  nextSortOrder: number;
  moduleIdOptions: SelectOption[];
  actionCatalog: string[];
  lobOptions: SelectOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [moduleId, setModuleId] = useState<string | null>(
    existingModule?.moduleId ?? null,
  );
  const [actions, setActions] = useState<string[]>(existingModule?.actions ?? []);
  const [requireIpaMatch, setRequireIpaMatch] = useState<boolean>(
    existingModule?.dataScope?.requireIpaMatch ?? false,
  );
  const [allowedLob, setAllowedLob] = useState<string[]>(
    existingModule?.dataScope?.allowedLineOfBusiness ?? [],
  );

  const [upsertModule, { loading, error }] = useMutation<unknown, UpsertPersonaModuleVars>(
    UPSERT_PERSONA_MODULE_MUTATION,
    { onCompleted: () => { onSaved(); onClose(); } },
  );

  // Action options are module-aware: the FE catalog for the chosen module (the canonical
  // vocabulary) unioned with any actions already stored on the module + across the DB.
  const actionOptions: SelectOption[] = useMemo(() => {
    const set = new Set<string>(actionCatalog);
    if (moduleId && moduleId in FEATURE_CATALOG) {
      for (const a of catalogActions(moduleId as ModuleId)) set.add(a);
    }
    for (const a of actions) set.add(a);
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((a) => ({ value: a, label: a }));
  }, [actionCatalog, moduleId, actions]);

  const submit = () => {
    if (!moduleId) return;
    // dataScope is a JSON string on the wire, and the backend upsert COALESCEs it
    // (omitting it PRESERVES the stored scope). So on EDIT we must always send the
    // explicit current scope — otherwise unchecking "Require IPA match" or clearing the
    // LOB list would be silently dropped. On ADD, omit when empty (nothing to preserve).
    const hasScope = requireIpaMatch || allowedLob.length > 0;
    const dataScope =
      existingModule || hasScope
        ? JSON.stringify({
            requireIpaMatch,
            ...(allowedLob.length > 0
              ? { allowedLineOfBusiness: allowedLob }
              : {}),
          })
        : undefined;
    void upsertModule({
      variables: {
        personaCode,
        moduleId,
        // Preserve the existing sort order on edit; append new modules at the end.
        sortOrder: existingModule?.sortOrder ?? nextSortOrder,
        actions: actions.length ? actions : undefined,
        dataScope,
        isActive: existingModule?.isActive ?? true,
      },
    });
  };

  // Portal to <body> so the fixed overlay is a top-level sibling of #root and
  // escapes any ancestor that establishes a containing block / compositing
  // layer (transform, filter, contain) — which otherwise re-rasterizes and
  // visibly degrades the background text. (See SwitchPatientModal / #122.)
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-lg font-bold mb-1">
          {existingModule ? "Edit Module" : "Add Module"}
        </h2>
        <p className="text-sm text-muted-foreground mb-4">Persona: <strong>{personaCode}</strong></p>
        <form
          onSubmit={(e) => { e.preventDefault(); submit(); }}
          className="flex flex-col gap-4"
        >
          <div>
            <label className="block text-sm font-medium mb-1">
              Module <span className="text-destructive">*</span>
            </label>
            <SearchableSelect
              value={moduleId}
              onChange={setModuleId}
              options={moduleIdOptions}
              // The module of an existing entry is its key — don't let an edit re-target it.
              disabled={!!existingModule}
              ariaLabel="Module"
              placeholder="Select a module…"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Actions</label>
            <SearchableSelect
              multiple
              value={actions}
              onChange={setActions}
              options={actionOptions}
              allowCustom
              ariaLabel="Actions"
              placeholder="Add actions…"
            />
          </div>

          <fieldset className="rounded-lg border border-border p-3">
            <legend className="text-sm font-medium px-1">Data Scope</legend>
            <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={requireIpaMatch}
                onChange={(e) => setRequireIpaMatch(e.target.checked)}
                className="rounded border-input"
              />
              Require IPA match
            </label>
            <label className="block text-sm font-medium mb-1">Line of business</label>
            <SearchableSelect
              multiple
              value={allowedLob}
              onChange={setAllowedLob}
              options={lobOptions}
              allowCustom
              ariaLabel="Allowed line of business"
              placeholder="Any (unrestricted)…"
            />
          </fieldset>

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error.message}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading || !moduleId}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 hover:bg-primary/90 transition-colors">
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

// ---- Module row with inline toggle + sort-order edit ----
function ModuleRow({
  personaCode,
  mod,
  onEdit,
  onRefresh,
}: {
  personaCode: string;
  mod: AcAdminPersonaModule;
  onEdit: () => void;
  onRefresh: () => void;
}) {
  const [editingSort, setEditingSort] = useState(false);
  const [sortInput, setSortInput] = useState(String(mod.sortOrder));

  const [upsertModule, { loading: upserting }] = useMutation<unknown, UpsertPersonaModuleVars>(
    UPSERT_PERSONA_MODULE_MUTATION,
    { onCompleted: onRefresh },
  );

  const [deactivateModule, { loading: deactivating }] = useMutation<DeactivatePersonaModuleResult, DeactivatePersonaModuleVars>(
    DEACTIVATE_PERSONA_MODULE_MUTATION,
    { onCompleted: onRefresh },
  );

  const busy = upserting || deactivating;

  const toggleActive = () => {
    if (mod.isActive) {
      void deactivateModule({ variables: { personaCode, moduleId: mod.moduleId } });
    } else {
      void upsertModule({
        variables: {
          personaCode,
          moduleId: mod.moduleId,
          sortOrder: mod.sortOrder,
          actions: mod.actions ?? undefined,
          isActive: true,
        },
      });
    }
  };

  const commitSortOrder = () => {
    const parsed = parseInt(sortInput, 10);
    if (!isNaN(parsed) && parsed !== mod.sortOrder) {
      void upsertModule({
        variables: {
          personaCode,
          moduleId: mod.moduleId,
          sortOrder: parsed,
          actions: mod.actions ?? undefined,
          isActive: mod.isActive,
        },
      });
    }
    setEditingSort(false);
  };

  return (
    <div
      className={
        "flex items-center gap-3 rounded-lg border bg-card px-3 py-2 transition-opacity " +
        (mod.isActive ? "border-border" : "border-border/50 opacity-60")
      }
    >
      <span className={"font-mono text-sm font-semibold flex-1 " + (mod.isActive ? "" : "line-through text-muted-foreground")}>
        {mod.moduleId}
      </span>

      {/* Inline sort-order editor */}
      {editingSort ? (
        <input
          type="number"
          value={sortInput}
          onChange={(e) => setSortInput(e.target.value)}
          onBlur={commitSortOrder}
          onKeyDown={(e) => { if (e.key === "Enter") commitSortOrder(); if (e.key === "Escape") setEditingSort(false); }}
          autoFocus
          className="w-14 rounded border border-input bg-background px-1.5 py-0.5 text-xs text-center focus:outline-none focus:ring-1 focus:ring-ring"
        />
      ) : (
        <button
          type="button"
          onClick={() => { setSortInput(String(mod.sortOrder)); setEditingSort(true); }}
          title="Click to edit sort order"
          className="text-xs text-muted-foreground hover:text-foreground hover:bg-accent px-1.5 py-0.5 rounded transition-colors"
        >
          #{mod.sortOrder}
        </button>
      )}

      {(mod.actions?.length ?? 0) > 0 && (
        <div className="flex gap-1 flex-wrap">
          {mod.actions!.map((a) => (
            <span key={a} className="inline-flex items-center rounded bg-secondary px-1.5 py-0.5 text-xs">
              {a}
            </span>
          ))}
        </div>
      )}

      {/* Edit module (opens the Add/Edit modal prefilled) */}
      <button
        type="button"
        onClick={onEdit}
        title="Edit module"
        aria-label={`Edit ${mod.moduleId} module`}
        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
      >
        <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
      </button>

      {/* Active / inactive toggle */}
      <button
        type="button"
        disabled={busy}
        onClick={toggleActive}
        title={mod.isActive ? "Deactivate module" : "Re-activate module"}
        className={
          "p-1 rounded transition-colors disabled:opacity-40 " +
          (mod.isActive
            ? "text-green-600 dark:text-green-400 hover:bg-destructive/10 hover:text-destructive"
            : "text-muted-foreground hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-600")
        }
      >
        {mod.isActive
          ? <ToggleRight className="w-4 h-4" aria-hidden="true" />
          : <ToggleLeft className="w-4 h-4" aria-hidden="true" />}
      </button>
    </div>
  );
}

// ---- Persona Row ----
function PersonaRow({
  persona,
  moduleIdOptions,
  actionCatalog,
  lobOptions,
  onRefresh,
}: {
  persona: AcAdminPersona;
  moduleIdOptions: SelectOption[];
  actionCatalog: string[];
  lobOptions: SelectOption[];
  onRefresh: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  // null = closed; { module?: AcAdminPersonaModule } = open (add when module undefined)
  const [editTarget, setEditTarget] = useState<
    { module?: AcAdminPersonaModule } | null
  >(null);

  const activeCount = persona.modules.filter((m) => m.isActive).length;

  // New modules append after the current max sort order (sort is no longer a modal input).
  const nextSortOrder =
    persona.modules.reduce((max, m) => Math.max(max, m.sortOrder), -1) + 1;

  return (
    <>
      <div className="border border-border rounded-xl overflow-hidden mb-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center gap-3 px-4 py-3 bg-card hover:bg-muted/30 transition-colors text-left"
        >
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
          )}
          <span className="font-semibold">{persona.displayName}</span>
          <span className="text-xs text-muted-foreground font-mono">{persona.code}</span>
          <span className="ml-auto text-xs text-muted-foreground">
            {activeCount}/{persona.modules.length} active
          </span>
        </button>
        {expanded && (
          <div className="border-t border-border bg-muted/10 px-4 py-3">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-muted-foreground">
                Modules <span className="text-xs">(click # to edit sort order, toggle right/left to activate)</span>
              </p>
              <button
                type="button"
                onClick={() => setEditTarget({})}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-border hover:bg-accent transition-colors"
              >
                <Pencil className="w-3 h-3" />
                Add Module
              </button>
            </div>
            {persona.modules.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No modules configured.</p>
            ) : (
              <div className="space-y-2">
                {persona.modules
                  .slice()
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((mod) => (
                    <ModuleRow
                      key={mod.moduleId}
                      personaCode={persona.code}
                      mod={mod}
                      onEdit={() => setEditTarget({ module: mod })}
                      onRefresh={onRefresh}
                    />
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
      {editTarget && (
        <UpsertModuleModal
          personaCode={persona.code}
          existingModule={editTarget.module}
          nextSortOrder={nextSortOrder}
          moduleIdOptions={moduleIdOptions}
          actionCatalog={actionCatalog}
          lobOptions={lobOptions}
          onClose={() => setEditTarget(null)}
          onSaved={onRefresh}
        />
      )}
    </>
  );
}

// ---- Main Page ----
export default function PersonasPage() {
  // cache-first: revisiting this tab reads the warm cache instantly.
  // previousData keeps the list visible during an explicit refetch().
  const {
    data: listData,
    previousData: listPrevData,
    loading: listLoading,
    error: listError,
    refetch: refetchList,
  } = useQuery<ListPersonasResult>(LIST_PERSONAS_QUERY, {
    fetchPolicy: "cache-first",
    errorPolicy: "all",
  });

  // Deterministic dropdown catalogs for the module modal. errorPolicy:"all" so a
  // schema-miss degrades to canonical-only options rather than blocking the page.
  const { data: optionsData } = useQuery<PersonaModuleOptionsResult>(
    PERSONA_MODULE_OPTIONS_QUERY,
    { fetchPolicy: "cache-first", errorPolicy: "all" },
  );

  const moduleIdOptions: SelectOption[] = useMemo(
    () =>
      (optionsData?.personaModuleOptions.moduleIds ?? []).map((m) => ({
        value: m,
        label: m,
      })),
    [optionsData],
  );
  const actionCatalog: string[] = useMemo(
    () => optionsData?.personaModuleOptions.actions ?? [],
    [optionsData],
  );
  const lobOptions: SelectOption[] = useMemo(
    () =>
      (optionsData?.personaModuleOptions.lineOfBusiness ?? []).map((l) => ({
        value: l,
        label: l,
      })),
    [optionsData],
  );

  const effectiveListData = listData ?? listPrevData;
  const listFailed = !!listError && !effectiveListData?.listPersonas;
  const personas: AcAdminPersona[] = effectiveListData?.listPersonas ?? [];

  return (
    <div>
      <AdminWall error={listError} screenLabel="Personas">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold">Personas</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              View personas and configure their modules. Assign personas to
              users from the Users tab.
            </p>
            {listFailed && !isAuthWallError(listError) && (
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1">
                <strong>Note:</strong> listPersonas query not yet on schema.
                The backend needs to expose a <code>listPersonas</code> query.
              </p>
            )}
          </div>
        </div>

        {listLoading && personas.length === 0 ? (
          <LoadingSpinner fullScreen={false} label="Loading personas..." />
        ) : personas.length === 0 ? (
          <p className="text-muted-foreground text-sm">No personas found.</p>
        ) : (
          <div>
            {personas.map((p) => (
              <PersonaRow
                key={p.code}
                persona={p}
                moduleIdOptions={moduleIdOptions}
                actionCatalog={actionCatalog}
                lobOptions={lobOptions}
                onRefresh={() => void refetchList()}
              />
            ))}
          </div>
        )}
      </AdminWall>
    </div>
  );
}
